/**
 * Bottom-of-screen banner that surfaces "a new build is ready" without
 * forcing a reload. Dispatched by src/web/lib/pwa-update.ts when a
 * waiting Service Worker installs while the tab is visible.
 *
 * Hidden tabs are reloaded silently in pwa-update.ts — by the time
 * this banner mounts, the user is looking at the page, so we let
 * them pick the moment (they might be mid-comment, mid-chat, or
 * mid-share-link composer).
 */

import { useEffect, useState } from 'react';
import { applyUpdate } from '../lib/pwa-update';

export function UpdateAvailableBanner() {
  const [show, setShow] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    const onAvailable = () => setShow(true);
    window.addEventListener('synfolio:update-available', onAvailable);
    return () => window.removeEventListener('synfolio:update-available', onAvailable);
  }, []);

  if (!show) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex justify-center px-4"
    >
      <div className="pointer-events-auto flex max-w-[420px] items-center gap-3 rounded-full border border-ink-rim bg-ink-2 px-4 py-2 shadow-lg shadow-black/40">
        <span className="text-xs text-cream">A new version of SynFolio is ready.</span>
        <button
          type="button"
          className="btn btn-primary text-[11px]"
          disabled={applying}
          onClick={async () => {
            setApplying(true);
            await applyUpdate();
            // applyUpdate triggers a reload; this state only matters
            // for the brief activation window before the reload.
          }}
        >
          {applying ? 'Refreshing…' : 'Refresh'}
        </button>
        <button
          type="button"
          aria-label="Dismiss"
          className="text-xs text-cream-3 hover:text-cream"
          onClick={() => setShow(false)}
        >
          ×
        </button>
      </div>
    </div>
  );
}
